import { Button } from 'antd';
import { ROUTES } from 'helpers/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useUiState } from 'state/ui-state/ui-state';
import { useNotificationStore } from 'state/notificationstate/notificationstate';
import { saveConfiguration } from 'viewer/api';
import { ViewRunningTotalFooter } from './view-running-total-footer';
import { AnimatedRightArrow } from 'components/animated-arrow/animated-right-arrow';
import { useHover } from 'helpers/useHover';
import Typography from 'helpers/fonts';
import { AnimatedLeftArrow } from 'components/animated-arrow/animated-left-arrow';
import './running-total.css';
import { useSmallScreen } from 'helpers/isSmallScreen';

export interface RunningTotalProps {
  style?: React.CSSProperties;
}

type Steps = Record<
  string,
  {
    next: { action: () => void; label: string; arrow: boolean };
    backAction?: () => void;
    viewQuote: boolean;
    viewRunningTotalFooter: boolean;
  }
>;

const RunningTotal: React.FunctionComponent<RunningTotalProps> = ({
  style,
}: RunningTotalProps) => {
  const { t } = useTranslation();
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );
  const { pathname } = useLocation();
  const history = useHistory();
  const { lastLocation, viewerBusy } = useUiState();
  const isSmallScreen = useSmallScreen();

  const placeOrder = async () => {
    try {
      const postConfigResponse = await saveConfiguration(false);
      if (postConfigResponse) {
        window.location.href = postConfigResponse.redirectURL;
      }
    } catch (error) {
      setNotification(error.message, 'error', 'small');
    }
  };

  const goBack = () =>
    lastLocation ? history.goBack() : history.push(ROUTES.BUILD_SCREEN);

  const steps: Steps = {
    [ROUTES.BUILD_SCREEN]: {
      next: {
        action: () => history.push(ROUTES.MATERIAL_SCREEN),
        label: 'runningTotal.nextMaterial',
        arrow: true,
      },
      viewQuote: true,
      viewRunningTotalFooter: true,
    },
    [ROUTES.MATERIAL_SCREEN]: {
      next: {
        action: () => history.push(ROUTES.OPTIONS_SCREEN),
        label: 'runningTotal.nextOptions',
        arrow: true,
      },
      viewQuote: true,
      viewRunningTotalFooter: true,
    },
    [ROUTES.OPTIONS_SCREEN]: {
      next: {
        action: () => history.push(ROUTES.QUOTE_SCREEN),
        label: 'runningTotal.reviewFinalDesign',
        arrow: true,
      },
      viewQuote: false,
      viewRunningTotalFooter: true,
    },
    [ROUTES.QUOTE_SCREEN]: {
      next: {
        action: placeOrder,
        label: 'runningTotal.placeOrder',
        arrow: false,
      },
      viewQuote: false,
      viewRunningTotalFooter: false,
      backAction: goBack,
    },
  };

  const step = steps[pathname];
  const [ref, isHovered] = useHover();
  const [backRef, backIsHovered] = useHover();
  return (
    <div
      style={{
        ...style,
      }}
    >
      {step.viewRunningTotalFooter && <ViewRunningTotalFooter />}

      <div style={{ width: '100%', height: isSmallScreen ? '42px' : '50px' }}>
        {step.viewQuote && (
          <Button
            onClick={() => history.push(ROUTES.QUOTE_SCREEN)}
            style={{
              width: '33.3333%',
              height: '100%',
              border: 'none',
              borderRadius: 0,
              fontFamily: 'Gibson Medium',
              textTransform: 'uppercase',
            }}
            type="default"
          >
            {t('runningTotal.viewQuote')}
          </Button>
        )}
        {step.backAction && (
          <Button
            onClick={step.backAction}
            style={{
              width: '33.3333%',
              height: '100%',
              border: 'none',
              borderRadius: 0,
              fontFamily: 'Gibson Medium',
              textTransform: 'uppercase',
            }}
            className="back-button"
            ref={backRef}
            type="default"
          >
            <AnimatedLeftArrow hovered={backIsHovered} />
            <span style={{ marginLeft: '44px' }} />
            {t('runningTotal.back')}
          </Button>
        )}
        <Button
          style={{
            width: step.viewQuote || step.backAction ? '66.6%' : '100%',
            borderRadius: 0,
            height: '100%',
            textTransform: 'uppercase',
          }}
          className="next-button"
          loading={viewerBusy}
          type="primary"
          onClick={step.next.action}
          ref={ref}
        >
          <Typography
            styleid={4}
            style={{ color: Theme['@white'], fontFamily: 'Gibson Medium' }}
          >
            <span style={{ marginRight: '24px' }}>
              {t(step.next.label)?.toUpperCase()}
            </span>
          </Typography>
          {step.next.arrow && <AnimatedRightArrow hovered={isHovered} />}
        </Button>
      </div>
    </div>
  );
};

export default RunningTotal;
